<template>
  <div>
    <a-spin :spinning="loading">
      <div v-if="!mfaEnabled">
        <a-alert
          :message="$t('mfa.notEnabled')"
          type="info"
          show-icon
          class="mb-4"
        >
          <template slot="description">
            {{ $t("mfa.notEnabledDescription") }}
          </template>
        </a-alert>
        <a-button type="primary" @click="showSetupModal = true">
          <a-icon type="lock" />
          {{ $t("mfa.enable") }}
        </a-button>
      </div>

      <div v-else>
        <a-alert
          :message="$t('mfa.enabled')"
          type="success"
          show-icon
          class="mb-4"
        >
          <template slot="description">
            <div>
              <strong>{{ $t("mfa.method") }}:</strong>
              {{
                mfaMethod === "app" ? $t("mfa.authenticatorApp") : $t("mfa.sms")
              }}
            </div>
            <div v-if="mfaMethod === 'sms' && userPhone" class="mt-2">
              <strong>{{ $t("mfa.phoneNumber") }}:</strong> {{ userPhone }}
            </div>
          </template>
        </a-alert>
        <a-button type="danger" @click="handleDisable">
          <a-icon type="unlock" />
          {{ $t("mfa.disable") }}
        </a-button>
      </div>
    </a-spin>

    <!-- Setup Modal -->
    <a-modal
      :title="$t('mfa.setup')"
      :visible="showSetupModal"
      :footer="null"
      width="60%"
      @cancel="handleCancelSetup"
    >
      <MFASetup :key="showSetupModal" :user-phone="userPhone" @enabled="handleEnabled" />
    </a-modal>

    <!-- Disable Confirmation Modal -->
    <a-modal
      :title="$t('mfa.disable')"
      :visible="showDisableModal"
      @ok="confirmDisable"
      @cancel="handleCancelDisable"
      :confirmLoading="disabling"
      :okText="$t('mfa.confirmDisable')"
      :cancelText="$t('action.fermer')"
    >
      <div>
        <p class="mb-3">{{ $t("mfa.disableConfirmation") }}</p>
        <p class="mb-3 text-warning">
          <a-icon type="exclamation-circle" />
          {{ $t("mfa.passwordRequiredToDisable") }}
        </p>
        <a-form>
          <a-form-item
            :label="$t('login.password')"
            :validate-status="passwordError ? 'error' : ''"
            :help="passwordError || undefined"
          >
            <a-input-password
              v-model="disablePassword"
              :placeholder="$t('mfa.enterPasswordToDisable')"
              autocomplete="current-password"
              @pressEnter="confirmDisable"
            />
          </a-form-item>
        </a-form>
      </div>
    </a-modal>
  </div>
</template>

<script>
import * as jwt from "@/services/jwt";
import apiClient from "@/services/axios";
import MFASetupComponent from "../MFASetup/index.vue";
import Vue from "vue";

export default {
  name: "MFASettings",
  components: {
    MFASetup: MFASetupComponent,
  },
  props: {
    userPhone: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: true,
      mfaEnabled: false,
      mfaMethod: null,
      showSetupModal: false,
      showDisableModal: false,
      disabling: false,
      disablePassword: "",
      passwordError: "",
    };
  },
  async created() {
    await this.fetchMFAStatus();
  },
  methods: {
    async fetchMFAStatus() {
      this.loading = true;
      try {
        // Get user info which includes MFA status
        const userState = this.$store.state.user;
        const response = await apiClient.get(
          `/users/${userState.userName}?includeMFA=true`,
          {
            headers: {
              db: "Root",
            },
          }
        );

        this.mfaEnabled = response.data.mfa?.enabled || false;
        this.mfaMethod = response.data.mfa?.method || null;
      } catch (error) {
        console.error("Error fetching MFA status:", error);
        this.mfaEnabled = false;
        this.mfaMethod = null;
      } finally {
        this.loading = false;
      }
    },
    handleEnabled(data) {
      this.mfaEnabled = true;
      this.mfaMethod = data.method;
      this.$emit("mfa-status-changed", { enabled: true, method: data.method });
    },
    handleCancelSetup() {
      this.fetchMFAStatus();
      this.showSetupModal = false;
    },
    handleDisable() {
      this.disablePassword = "";
      this.passwordError = "";
      this.showDisableModal = true;
    },
    handleCancelDisable() {
      this.disablePassword = "";
      this.passwordError = "";
      this.showDisableModal = false;
    },
    async confirmDisable() {
      // Validate password
      if (!this.disablePassword || this.disablePassword.trim() === "") {
        this.passwordError = this.$t("mfa.passwordRequired");
        return;
      }

      this.passwordError = "";
      this.disabling = true;
      try {
        await jwt.disableMFA(this.disablePassword);
        this.mfaEnabled = false;
        this.mfaMethod = null;
        this.disablePassword = "";
        this.showDisableModal = false;
        this.$emit("mfa-status-changed", { enabled: false, method: null });
        Vue.prototype.$notification.success({
          message: this.$t("mfa.disabled"),
          description: this.$t("mfa.disabledSuccess"),
        });
      } catch (error) {
        // Check if error is due to invalid password
        if (
          error.response?.data?.error?.message
            ?.toLowerCase()
            .includes("password")
        ) {
          this.passwordError = this.$t("mfa.invalidPassword");
        }
        // Other errors are handled in service
      } finally {
        this.disabling = false;
      }
    },
  },
};
</script>

<style scoped>
.mb-4 {
  margin-bottom: 16px;
}

.mt-2 {
  margin-top: 8px;
}
</style>
